<script lang="ts" setup>
  import { computed, onMounted } from 'vue'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import BaseButtonLink from '@/shared/components/BaseButtonLink/BaseButtonLink.vue'
  import FileDownloadIcon from '@/shared/components/icons/FileDownloadIcon.vue'
  import TicketIcon from '@/shared/components/icons/TicketIcon.vue'
  import SuccessBanner from '@/shared/components/SuccessBanner/SuccessBanner.vue'
  import DomainAnnotation from '@/shared/components/DomainAnnotation/DomainAnnotation.vue'
  import type { PurchaseEventParams } from '@/shared/services/GoogleAnalyticsService'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { useQuoteStore, useUserStore, useCommonStore } from '@/shared/store'
  import { TravelDirection } from '@/shared/types/common.types'
  import {
    RAMADAN_CAMPAIGN_2025_DEV,
    RAMADAN_CAMPAIGN_2025_PROD,
  } from '@/shared/constants/features.constants'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  import { useIsProduction } from '@/shared/composables/useIsProduction'
  import BonusesEarnedInfo from '@qicgroupdoha/loyalty-components/components/BonusesEarnedInfo'
  import DownloadAppBanner from '@qicgroupdoha/loyalty-components/components/DownloadAppBanner'
  import { usePageContext } from '@/shared/composables'
  import SuccessAiraloBanner from './components/SuccessAiraloBanner/SuccessAiraloBanner.vue'
  import SuccessOoredooBanner from './components/SuccessOoredooBanner'

  interface Props {
    typeOfTravel: TravelDirection
    policyNumber: string
    email: string
    policyLink: string
    cyberPolicyLink?: string
    recieptLink: string
    isUserRegistered: boolean
    earnedBonuses: number
    isAiraloBanner: boolean
    isOoredooBanner?: boolean
  }

  const props = defineProps<Props>()

  const DOWNLOAD_APP_LINK = import.meta.env.VUE_APP_DOWNLOAD_APP_LINK

  const gaService = useGoogleAnalytics()
  const userStore = useUserStore()
  const quoteStore = useQuoteStore()
  const commonStore = useCommonStore()
  const isProduction = useIsProduction()
  const { getFeatureValue } = useFeaturesStore()
  const { isWebView } = usePageContext()
  const isRamadanCampaign = computed(() => {
    if (isProduction) {
      return getFeatureValue(RAMADAN_CAMPAIGN_2025_PROD)
    }

    return getFeatureValue(RAMADAN_CAMPAIGN_2025_DEV)
  })

  const coinsInstruction = computed(() =>
    props.isUserRegistered
      ? 'bonuses.popup.description.user_registered'
      : 'bonuses.popup.description.user_not_registered'
  )

  const sendDownloadPolicy = () => {
    gaService.sendDownloadDoc('click_download_policy', props.typeOfTravel, props.policyNumber)
  }

  const sendDownloadCyberPolicy = () => {
    gaService.sendPolicyDownload(props.policyNumber, props.typeOfTravel, 'cyber_coverage_policy')
  }

  const sendReceipt = () => {
    gaService.sendDownloadDoc('click_receipt', props.typeOfTravel, props.policyNumber)
  }

  const sendGoToStore = () => {
    gaService.sendGoToStore(props.policyNumber, props.typeOfTravel)
  }

  const sendSeeEarnedCoins = () => {
    gaService.sendSeeEarnedCoins(props.policyNumber, props.typeOfTravel, props.earnedBonuses)
  }

  const sendPurchaseEvent = () => {
    const params: PurchaseEventParams = {
      category: props.typeOfTravel,
      coupon: userStore.promocode,
      transaction_id: quoteStore.quoteNo,
      policy_id: props.policyNumber,
      num_of_travelers: quoteStore.insuredTravelers,
      total_price: quoteStore.totalPrice,
      choosen_card: userStore.paymentMethod,
      countries: [],
    }

    if (props.typeOfTravel === TravelDirection.OUTBOUND) {
      params.countries = userStore.destination.map((d) => d.id)
      params.addons_price = quoteStore.addonsPrice
      params.list_of_addons = quoteStore.addonsList
      params.number_of_addons = quoteStore.activeAddons
    } else {
      params.countries = [userStore.residenceCountry?.name || '']
    }

    gaService.sendPurchaseEvent(params)
  }

  onMounted(() => {
    commonStore.getContacts()

    if (quoteStore.isSentPurchaseAnalyticsEvent) return

    gaService.sendDownloadPolicyEvent(props.typeOfTravel, props.policyNumber)
    sendPurchaseEvent()

    quoteStore.setIsSentPurchaseAnalyticsEvent(true, true)
    userStore.clearPromocode(true)
    sessionStorage.removeItem('travel_affinity-code')
    userStore.setIsHeroSection(false)
  })
</script>

<template>
  <div :class="{ 'success-container--with-banner': props.isAiraloBanner || props.isOoredooBanner }">
    <FlowCard :with-button="false">
      <div class="success-insured">
        <img
          src="@/shared/assets/images/insurance-result.png"
          alt=""
          class="success-insured__image"
        />
        <div class="success-insured__content">
          <div>
            <h1 class="success-insured__title">{{ $t('travel.success_screen.header') }}</h1>
            <p class="success-insured__policy">
              {{ $t('travel.success_screen.email_sent') }}:
              <span class="success-insured__policy-email">{{ props.email }}</span>
            </p>
          </div>
          <BonusesEarnedInfo
            v-if="earnedBonuses > 0"
            :amount="earnedBonuses"
            :app-url="DOWNLOAD_APP_LINK"
            :app-button-text="$t('common.go_to_app.button')"
            :button-text="$t('success_page.bonuses.label.see_more')"
            :modal-title="$t(coinsInstruction)"
            @go-to-app="sendGoToStore"
            @see-more="sendSeeEarnedCoins"
          />
          <div class="success-insured__links">
            <BaseButtonLink
              :href="policyLink"
              target="_blank"
              class="success-insured__links-el"
              @click="sendDownloadPolicy"
            >
              <FileDownloadIcon />
              {{ $t('travel.success_screen.policy_download') }}
            </BaseButtonLink>

            <BaseButtonLink
              v-if="cyberPolicyLink"
              :href="cyberPolicyLink"
              target="_blank"
              class="success-insured__links-el"
              @click="sendDownloadCyberPolicy"
            >
              <FileDownloadIcon />
              {{ $t('travel.success_screen.cyber_policy_download') }}
            </BaseButtonLink>

            <BaseButtonLink
              :href="recieptLink"
              target="_blank"
              class="success-insured__links-el"
              @click="sendReceipt"
            >
              <TicketIcon />
              {{ $t('travel.success_screen.receipt_download') }}
            </BaseButtonLink>
          </div>

          <template v-if="!isWebView">
            <DownloadAppBanner
              v-if="typeOfTravel === TravelDirection.OUTBOUND && !isRamadanCampaign"
              :qr-code-alt="$t('ramadan.success_page.scan')"
              :title="$t('app_banner.manage_policy.desktop.title')"
              :subtitle="$t('app_banner.manage_policy.desktop.subtitle')"
              :app-url="DOWNLOAD_APP_LINK"
              :mobile-button-text="$t('common.go_to_app.button')"
              :mobile-title="$t('app_banner.manage_policy.mobile.title')"
              :mobile-subtitle="$t('app_banner.manage_policy.mobile.subtitle')"
              teleport-to="#download-banner-container"
              @go-to-app="sendGoToStore"
            />
            <SuccessBanner v-if="isRamadanCampaign" :policy-number="policyNumber" />
          </template>
        </div>
      </div>
    </FlowCard>

    <SuccessOoredooBanner v-if="props.isOoredooBanner" :policy-id="policyNumber" />
    <SuccessAiraloBanner v-else-if="props.isAiraloBanner" />
  </div>
  <DomainAnnotation class="success-domain-annotation" />
</template>

<style lang="scss">
  @import '@/shared/assets/styles/colors';

  .success-insured__policy-button {
    svg {
      path {
        fill: $white;
      }
    }
  }

  .success-domain-annotation {
    margin: 16px auto 0;
    max-width: 544px;
  }
</style>

<style lang="scss" setup>
  @import './SuccessInsuredWidget';
</style>
